import React, { useState,useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch,useSelector} from 'react-redux';
import PasswordStrengthBar from 'react-password-strength-bar';

import {useNotification} from 'config/Notification';

import { confirmNewPasswordService } from 'services/modules/idp/auth';

import {Visibility, VisibilityOff} from '@mui/icons-material';

import {
  Button,
  FormControl,
  FormHelperText, 
  IconButton, 
  InputAdornment,
  OutlinedInput
} from '@mui/material';

function NewPasswordConfirmation() {
  // State variables
  const [formData, setFormData] = useState({
    username: '',
    newPassword: '',
  });
  const [formErrors, setFormErrors] = useState({
    username: false,
    newPassword: false,
  });
  const [showPassword,setShowPassword]=useState(true);
  
  // Redux selectors
  const newPasswordStatusContainer = useSelector((state) => state.auth.newPasswordStatus);
  const errorsContainer = useSelector((state) => state.auth.errors);
  
  // React Router
  const navigate=useNavigate();  
  const location=useLocation();
  const searchParams = new URLSearchParams(location.search);
  const session = searchParams.get('session');
 
  // Redux dispatch
  const dispatch = useDispatch();
  
  // Notification 
  const {openNotification}=useNotification();

  // useEffect hook to handle newpassword status and redirection to login
  useEffect(() => {
    if (newPasswordStatusContainer) {
      openNotification('Password updated successfully', 'success');
      navigate('/login')
    }
  },// eslint-disable-next-line react-hooks/exhaustive-deps
  [newPasswordStatusContainer]);
 
  //Method returns score based on complexity
  const getPasswordScore = () => {
  
    const length = formData.newPassword.length;
    const hasNumber = /\d/.test(formData.newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword);
    const hasUpperCase = /[A-Z]/.test(formData.newPassword);

    if (length >= 8 && hasNumber && hasSpecialChar && hasUpperCase) {
      return 3; // High
    } 
    else if (length >= 8 && (hasNumber || hasSpecialChar || hasUpperCase)) {
      return 2; // Medium
    }
    else {
      return 1; // Low
    }
  };

  //Handles the input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  // Method updates the given password if the score is greater than or equals to two
  const handleUpdatePassword = (e) => {
    const passwordScore = getPasswordScore();
    const emptyFields = Object.entries(formData)
    .filter(([name, value]) => value === '')
    .map(([name]) => name);

    if (emptyFields.length > 0) {
      openNotification('Please fill in the required fields', 'error');
      return;
    }
  
    const updatedFormData = { ...formData };
    updatedFormData.session=session;
    if (passwordScore >= 2) {
      dispatch(confirmNewPasswordService(updatedFormData));
    } 
    else {
      openNotification('Password not strong enough', 'error');
    }
    e.preventDefault();
  };
  
  // This method display errors if form validation is not satisfied
  const handleErrors = (field) => {
    setFormErrors({
      ...formErrors,
      [field]: true
    });
  }

  // Method returns true if there are any empty fields
  const isFieldEmpty = (field) => {
    return formData[field].trim() === '';
  }

  return (
    <div className="newPassword-bg">
      <header>
        <img
          src="../assets/rhymtech_logo_2.png"
          alt="Logo"
          height="50px"
          className='newPassHeaderLogo'
        />
      </header>
      <div className="newPasswordCard-container">
        <div className="newPassConf-card newPassConf-image">
          <div className="newPassConf-section">
            <FormControl fullWidth>
              <OutlinedInput
                fullWidth
                name="username"
                className="newPasswordOutlinedInput" 
                value={formData.username}
                placeholder="Username"
                onChange={handleInputChange}
                onBlur={() => handleErrors('username')}
                error={formErrors.username && isFieldEmpty('username')}
              />

              {formErrors.username && isFieldEmpty('username') && (
                <FormHelperText className="text-bold" error>Field is required</FormHelperText>
              )}
            </FormControl>
            <FormControl  fullWidth>
              <OutlinedInput       
                fullWidth
                className="newPasswordOutlinedInput" 
                type={showPassword ? 'password' : 'text'}
                name="newPassword"
                value={formData.newPassword}
                placeholder="Password"
                onChange={handleInputChange}
                onBlur={() => handleErrors('newPassword')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      edge="end"
                    >
                      {showPassword? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                error={formErrors.newPassword && isFieldEmpty('newPassword')}
              />
              {formErrors.newPassword && isFieldEmpty('newPassword') && (
                <FormHelperText className='text-bold' error>Field is required</FormHelperText>
              )}
            </FormControl>

            <div className="password-strength-bar">
              <PasswordStrengthBar password={formData.newPassword} barColors={['#ccc', '#f44336', '#ff9800', '#1fd655', '#00c04b']}/>
            </div>
            <p className="error-message">{errorsContainer && errorsContainer.data ? errorsContainer.data.message : ''}</p>
            <div className="btn-container">
              <Button variant="contained" color="primary" onClick={handleUpdatePassword}>
                Update password
              </Button>
            </div>
          </div>

          <div className="information">
            <p className="text-h6 text-white text-bold q-ma-none"> Don't have an account with us? </p>
            <p className="text-white q-mt-sm"> Our yearly cyber health check plan will help you run your business “securely” at an affordable cost </p>
            <a href="mailto:crs.support@rhymtech.com" target="_blank" rel="noreferrer">
              <div className="btn-container">
                <Button  variant="contained">Reach our experts </Button>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewPasswordConfirmation;
